import React, { useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { zusToast } from '@zep/toast';
import { LockIcon } from '@/icons';
import { useRemoveFolderItem } from '@/shared/features/quiz-folder/api';
import { FOLDER_COPY } from '@/shared/features/quiz-folder/consts/copy';
import { useShowSaveToFolderModal } from '@/shared/features/quiz-folder/hooks/useShowSaveToFolderModal';
import { useUpsellingGuard, useUserPlanManager } from '@/shared/plan';
import { BuildProFeatureCoachmarkAnchor } from '../components/BuildProFeatureCoachmark';
import { FeatureUpsellModal } from '../components/FeatureUpsellModal';
import { useFocusTarget } from '../components/focus/useFocusTarget';
import { useBuildInfoContext } from '../context';
import { useBuildFormContext } from '../form/schema';
import { PublicFolderMoveNotice } from './PublicFolderMoveNotice';
import { ToggleOptionContainer } from './ToggleOptionContainer';
export const TogglePublicField = () => {
  const {
    watch,
    setValue
  } = useBuildFormContext();
  const {
    t
  } = useTranslation(['build']);
  const {
    userPlanManager
  } = useUserPlanManager();
  const upsellingGuard = useUpsellingGuard();
  const {
    action,
    originQuizSet
  } = useBuildInfoContext();
  const removeFolderItem = useRemoveFolderItem();
  const showSaveToFolderModal = useShowSaveToFolderModal();
  const isPublic = watch('isPublic');
  const featurePlanType = userPlanManager.getFeaturePlanType('PRIVATE_QUIZ');
  const ref = useFocusTarget<HTMLDivElement>('isPublic');

  /*
    공개 폴더에는 공개 퀴즈만 담긴다. 그래서 그 안의 퀴즈는 **여기서 바로 숨길 수 없고**
    먼저 위치를 옮겨야 한다(R-27). 서버도 같은 전제로 `QUIZSET_FOLDER_MOVE_REQUIRED` 를 주며,
    그건 재시도 대상이 아니라 "위치를 먼저 바꾸라"는 뜻이다.
     `UPDATE` 로 좁히는 이유: `originQuizSet` 은 복제(`DUPLICATE`)에도 채워지는데 그건 **원본**이고,
    만들어질 복사본은 아직 어느 폴더에도 없다.
  */
  const [hasMovedOut, setHasMovedOut] = useState(false);
  const currentFolderId = originQuizSet?.folderId;
  const isInPublicFolder = action === 'UPDATE' && !hasMovedOut && !!currentFolderId && originQuizSet?.folderVisibility === 'PUBLIC';

  /*
    옮긴 뒤에도 퀴즈 상세 쿼리를 다시 읽지 않는다 — 그 응답은 빌드 폼의 초기값이라
    refetch 가 편집 중인 내용을 되돌릴 수 있다. 화면에 필요한 것은 "이제 막히지 않는다"뿐이다.
    비공개 전환은 자동으로 켜지 않는다. 사용자가 다시 누른다.
  */
  const handleMovedOut = () => setHasMovedOut(true);
  const moveToMyQuiz = () => {
    if (!currentFolderId || !originQuizSet) return;
    void (async () => {
      try {
        await removeFolderItem.mutateAsync({
          id: currentFolderId,
          quizsetId: originQuizSet.id
        });
        zusToast.success(FOLDER_COPY.moveToDefaultSuccess);
        handleMovedOut();
      } catch {
        zusToast.error(FOLDER_COPY.saveError);
      }
    })();
  };
  return <div ref={ref} className="flex flex-col gap-sm" tabIndex={0} data-sentry-component="TogglePublicField" data-sentry-source-file="TogglePublicField.tsx">
      <ToggleOptionContainer optionIcon={<LockIcon size="sm" fill={ICON_COLOR.neutral} />} optionTitle={t('build:hideFromExplore.title')} optionDescription={t('build:hideFromExplore.description')} isOn={!isPublic} planType={featurePlanType} isBadgeVisible={true} disabled={isInPublicFolder} onToggle={() => {
      logger.track('isPublic_select', {
        value: !isPublic
      });
      upsellingGuard(() => {
        setValue('isPublic', !isPublic, {
          shouldDirty: true,
          shouldTouch: true
        });
      }, {
        feature: 'PRIVATE_QUIZ',
        from: 'isPublic',
        moreConditionForGuard: isPublic === true,
        onGuard: () => {
          overlay.open(({
            unmount
          }) => <FeatureUpsellModal feature="PRIVATE_QUIZ" onClose={unmount} />);
        }
      });
    }} renderToggle={toggle => <BuildProFeatureCoachmarkAnchor feature="PRIVATE_QUIZ">
            {toggle}
          </BuildProFeatureCoachmarkAnchor>} data-sentry-element="ToggleOptionContainer" data-sentry-source-file="TogglePublicField.tsx" />
      {isInPublicFolder && originQuizSet && <PublicFolderMoveNotice isMoving={removeFolderItem.isPending} onSelectFolder={() => void showSaveToFolderModal({
      id: originQuizSet.id,
      intent: 'move',
      currentFolderId
    }, {
      privateOnly: true,
      onMoved: handleMovedOut
    })} onMoveToMyQuiz={moveToMyQuiz} />}
    </div>;
};